import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line
import { Container, ContentWithPaddingXl } from "components/misc/Layouts.js";
import { SectionHeading } from "components/misc/Headings.js";
import { PrimaryButton as PrimaryButtonBase } from "components/misc/Buttons.js";
import { ReactComponent as SvgDecoratorBlob1 } from "images/svg-decorator-blob-5.svg";
import { ReactComponent as SvgDecoratorBlob2 } from "images/svg-decorator-blob-7.svg";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async"; // ✅ Import Helmet
import AnimationRevealPage from 'helpers/AnimationRevealPage';
import Light from 'components/headers/light';
import LightAdmin from 'components/headers/lightAdmin';
import Footerr from "components/footers/Footer";
import ScrollToTop from "components/ScrollToTop";
import { useTranslation } from "react-i18next";
import SearchBar from "./SearchBar";

const HeaderRow = tw.div`flex justify-center items-center flex-col w-full`;
const Header = styled(SectionHeading)`
  width: 100%;
  text-align: center !important;
  font-size: 30px !important;
  font-weight: 600;
  font-family: 'Urbanist', sans-serif !important;
  line-height: 1.2;
  align-self: center;
  margin-left: 0;
  margin-bottom: 20px;
`;

const TabContent = tw(motion.div)`mt-6 flex flex-wrap sm:-mr-10 md:-mr-6 lg:-mr-12`;
const CardContainer = tw.div`
  mt-10 w-full sm:w-1/2 md:w-1/2 lg:w-1/4 xl:w-1/4 sm:pr-4 md:pr-4 lg:pr-6 xl:pr-6
`;
const Card = tw(motion.a)`rounded-b block max-w-xs mx-auto sm:max-w-none sm:mx-0 mb-0`;
const CardImageContainer = styled.div`
  ${props => css`background-image: url("${props.imageSrc}");`}
  ${tw`h-56 xl:h-64 bg-center relative rounded-t transition-transform duration-300 ease-in-out group-hover:scale-105`}
  background-size: contain;
  background-repeat: no-repeat;
  background-color: #fff;
`;

const CardButton = tw(PrimaryButtonBase)`
  text-sm flex justify-center items-center mt-4 rounded-full px-10 py-2
`;
const CardButtonContainer = tw.div`
  flex justify-center items-center mt-2 w-auto
`;

const CardText = tw.div`p-4 text-gray-900`;
const CardTitle = tw.h5`text-sm font-bold text-center text-[#000000]`;
const CardPrice = tw.p`mt-2 font-semibold text-center text-[#4e7662] text-[14px]`;

const DecoratorBlob1 = styled(SvgDecoratorBlob1)`
  ${tw`pointer-events-none -z-20 absolute right-0 top-0 h-64 w-64 opacity-15 transform translate-x-2/3 -translate-y-12 text-pink-400`}
`;
const DecoratorBlob2 = styled(SvgDecoratorBlob2)`
  ${tw`pointer-events-none -z-20 absolute left-0 bottom-0 h-80 w-80 opacity-15 transform -translate-x-2/3 text-primary-500`}
`;

const PacksPage = () => {
  const role = localStorage.getItem("role");
  const [packs, setPacks] = useState([]);
  const [filteredPacks, setFilteredPacks] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`https://rehabwmg.online/api/packs?lang=${i18n.language}`)
      .then((response) => response.json())
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setPacks(list);
        setFilteredPacks(list);
      })
      .catch((error) => console.error("Error fetching packs:", error));
  }, [i18n.language]);

  const handleGoToProduct = (card) => {
    const productName = card.Name || "pack";
    const slug = productName.replace(/\s+/g, "-").toLowerCase();
    navigate(`/product-details/${card.ProductID}-${slug}`);
  };

  const slugify = (nameObj) => {
    const name = typeof nameObj === "object"
      ? nameObj[i18n.language] || nameObj.fr || ""
      : nameObj || "";
    return name.trim().replace(/\s+/g, "-").toLowerCase();
  };

  return (
    <>
      <Helmet>
        <html lang={i18n.language} />
        <title>Packs Promo Exclusifs Rehab Bio Maroc</title>
        <meta name="description" content="Voici enfin Packs Promo Exclusifs. Profitez d'une qualité exceptionnelle et respectueuse de l'environnement. Cliquez ici pour découvrir notre offre exclusive avec livraison rapide." />
        <link rel="canonical" href="https://rehabbio.ma/Packs" />

        {/* Open Graph Tags */}
        <meta property="og:title" content="Packs Promo Exclusifs Rehab Bio Maroc" />
        <meta property="og:description" content="Voici enfin Packs Promo Exclusifs. Profitez d'une qualité exceptionnelle et respectueuse de l'environnement. Cliquez ici pour découvrir notre offre exclusive avec livraison rapide." />
        <meta property="og:url" content="https://rehabbio.ma/Packs" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Rehab Bio" />
      </Helmet>

      <AnimationRevealPage>
        <ScrollToTop />
        {role === "supplier" ? <Light /> : role === "buyer" ? <LightAdmin /> : <Light />}
        <Container>
          <ContentWithPaddingXl>
            <HeaderRow>
              <Header>Packs Promo Exclusifs</Header>
              <SearchBar
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                language={i18n.language}
                allProducts={packs}
                onResults={setFilteredPacks}
              />
            </HeaderRow>

            <TabContent>
              {packs.length === 0 ? (
                <p style={{ textAlign: "center", width: "100%" }}>Téléchargement en cours…</p>
              ) : filteredPacks.length === 0 ? (
                <p style={{ textAlign: "center", width: "100%", color: "#9ca3af", marginTop: "2rem" }}>
                  {i18n.language === "ar" ? "لا توجد نتائج" : "Aucun pack trouvé"}
                </p>
              ) : (
                filteredPacks.map((card, index) => (
                  <CardContainer key={index}>
                    <Link to={`/product-details/${card.ProductID}-${slugify(card.Name)}`}>
                      <Card className="group" initial="rest" whileHover="hover" animate="rest">
                        <div onClick={() => handleGoToProduct(card)} style={{ cursor: "pointer" }}>
                          <CardImageContainer imageSrc={`https://rehabwmg.online/storage/${card.image}`} />
                        </div>
                        <CardText>
                          <CardPrice>
                            {(role === "supplier" || role === "influenceur" )
                              ? `${card.distributeur_price} ${t("currency")}`
                              : `${card.user_price} ${t("currency")}`}
                          </CardPrice>
                          <CardTitle>
                            {typeof card.Name === "object"
                              ? card.Name[i18n.language] || card.Name.fr
                              : card.Name}
                          </CardTitle>
                          <CardButtonContainer>
                            <CardButton onClick={() => handleGoToProduct(card)}>
                              <i className="bi bi-cart-plus"></i>{t('add_to_cart')}
                            </CardButton>
                          </CardButtonContainer>
                        </CardText>
                      </Card>
                    </Link>
                  </CardContainer>
                ))
              )}
            </TabContent>
          </ContentWithPaddingXl>

          <DecoratorBlob1 />
          <DecoratorBlob2 />
        </Container>
        <Footerr />
      </AnimationRevealPage>
    </>
  );
};

export default PacksPage;